import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Tooltip, keyframes } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LockOpenIcon from '@mui/icons-material/LockOpen';

const completePulseKeyframes = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(46, 125, 50, 0.55);
  }
  60% {
    transform: scale(1.15);
    box-shadow: 0 0 0 8px rgba(46, 125, 50, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(46, 125, 50, 0);
  }
`;

const unlockPulseKeyframes = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.5);
  }
  60% {
    transform: scale(1.15);
    box-shadow: 0 0 0 8px rgba(25, 118, 210, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

interface SectionCompleteToggleProps {
  isCompleted: boolean;
  onToggle: () => void;
  /** Shows progress inside the circle instead of the tick while the toggle's save is in flight. */
  isSaving?: boolean;
}

/**
 * Circular complete/lock toggle button shared across Magic Report sections.
 */
export const SectionCompleteToggle: React.FC<SectionCompleteToggleProps> = ({ isCompleted, onToggle, isSaving = false }) => {
  const [hovered, setHovered] = useState(false);
  const [playCompletePulse, setPlayCompletePulse] = useState(false);
  const [playUnlockPulse, setPlayUnlockPulse] = useState(false);
  const lastReactedCompletedRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    if (lastReactedCompletedRef.current === undefined) {
      lastReactedCompletedRef.current = isCompleted;
      return;
    }
    if (lastReactedCompletedRef.current === isCompleted) return;
    lastReactedCompletedRef.current = isCompleted;

    setHovered(false);
    let timeoutId: ReturnType<typeof setTimeout>;
    if (isCompleted) {
      setPlayCompletePulse(true);
      timeoutId = setTimeout(() => setPlayCompletePulse(false), 500);
    } else {
      setPlayUnlockPulse(true);
      timeoutId = setTimeout(() => setPlayUnlockPulse(false), 500);
    }
    return () => clearTimeout(timeoutId);
  }, [isCompleted]);

  // Saving makes the click a no-op, so the hover affordance is suppressed alongside it.
  const showHover = hovered && !isSaving;

  return (
    <Tooltip title={isSaving ? 'Saving…' : isCompleted ? 'Mark section incomplete' : 'Mark section complete'} placement='top'>
      <Box
        role='button'
        aria-pressed={isCompleted}
        aria-busy={isSaving}
        tabIndex={0}
        onClick={e => {
          e.stopPropagation();
          if (isSaving) return;
          onToggle();
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            if (isSaving) return;
            onToggle();
          }
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          borderRadius: '50%',
          cursor: isSaving ? 'default' : 'pointer',
          flexShrink: 0,
          border: '2px solid',
          borderColor: isCompleted ? (showHover ? 'primary.main' : 'success.main') : 'grey.400',
          bgcolor: isCompleted ? (showHover ? 'primary.main' : 'success.main') : showHover ? 'action.hover' : 'transparent',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          ...(!isCompleted && !isSaving && { '&:hover': { borderColor: 'primary.main' } }),
          ...(playCompletePulse && { animation: `${completePulseKeyframes} 0.5s ease-out` }),
          ...(playUnlockPulse && { animation: `${unlockPulseKeyframes} 0.5s ease-out` }),
        }}
      >
        <Box sx={{ position: 'relative', width: '1.1rem', height: '1.1rem' }}>
          {/* Save in flight: progress stands in for the tick until the new state is persisted */}
          {isSaving && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={14} thickness={5} sx={{ color: isCompleted ? 'background.paper' : 'primary.main' }} />
            </Box>
          )}
          {/* Completed + not hovered: solid check */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'opacity 0.15s ease, transform 0.15s ease',
              opacity: isCompleted && !showHover && !isSaving ? 1 : 0,
              transform: isCompleted && !showHover && !isSaving ? 'scale(1)' : 'scale(0.6)',
            }}
          >
            <CheckIcon sx={{ fontSize: '1.1rem', color: 'background.paper' }} />
          </Box>
          {/* Completed + hovered: unlock icon */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'opacity 0.15s ease, transform 0.15s ease',
              opacity: isCompleted && showHover ? 1 : 0,
              transform: isCompleted && showHover ? 'scale(1)' : 'scale(0.6)',
            }}
          >
            <LockOpenIcon sx={{ fontSize: '0.95rem', color: 'background.paper' }} />
          </Box>
          {/* Not completed + hovered: preview check */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'opacity 0.15s ease, transform 0.15s ease',
              opacity: !isCompleted && showHover ? 1 : 0,
              transform: !isCompleted && showHover ? 'scale(1)' : 'scale(0.6)',
            }}
          >
            <CheckIcon sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
};
